/**
 * API配置文件
 * 集中管理所有API请求相关的配置，方便后续调整
 */

// API基础URL配置
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://w3589.cn';
// API端点配置
export const API_ENDPOINTS = {
  // 套餐模块
  PLAN: {
    LIST: '/api/plan/list',
    DETAIL: (id) => `/api/plan/detail/${id}`,
    SUBMIT_PAYMENT: '/api/plan/submit-payment',
    PAYMENT_STATUS: (id) => `/api/plan/payment-status/${id}`,
    MY_PAYMENTS: '/api/plan/my-payments',
  },
  
  // 用户模块
  USER: {
    LOGIN: '/api/user/login',
    REGISTER: '/api/user/register',
    PROFILE: '/api/user/profile',
    LOGOUT: '/api/user/logout',
    STATISTICS: '/api/user/statistics',
  },
  
  // 斗篷模块
  CLOAK: {
    LIST: '/api/cloak/list',
    CREATE: '/api/cloak/create',
    UPDATE: (id) => `/api/cloak/update/${id}`,
    DELETE: (id) => `/api/cloak/delete/${id}`,
    COUNTRIES: '/api/cloak/countries',
    ACCESS_LOGS: '/api/cloak/access-logs',
    DETAIL: (id) => `/api/cloak/detail/${id}`,
  },
};

/**
 * 创建完整API URL
 * @param {string} endpoint - API端点路径
 * @returns {string} 完整的API URL
 */
export const createApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};

/**
 * 获取带有认证头的请求配置
 * @returns {Object} 包含Authorization头的配置对象
 */
export const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    }
  };
};

/**
 * 保存认证令牌
 * @param {Object} userData - 用户数据
 * @param {string} token - JWT 令牌
 * @param {boolean} rememberMe - 是否记住登录状态
 */
export const saveAuthData = (userData, token, rememberMe = false) => {
  if (!userData || !token) {
    console.error('[API] saveAuthData: 无效的认证数据', { 
      hasUserData: !!userData, 
      hasToken: !!token 
    });
    return;
  }
  
  try {
    // 构建存储数据
    const authData = {
      user: userData,
      token: token,
      timestamp: Date.now()
    };
    
    // 清除旧数据（确保不会有冲突）
    localStorage.removeItem('authData');
    sessionStorage.removeItem('authData');
    
    // 始终使用 localStorage 存储认证数据，以便在所有标签页中共享
    // 但如果用户不想记住登录状态，则设置一个短期过期标记
    const authDataStr = JSON.stringify(authData);
    localStorage.setItem('authData', authDataStr);
    localStorage.setItem('auth_remember', rememberMe.toString());
    
    console.log('[API] saveAuthData: 数据已成功保存到 localStorage，记住登录状态:', rememberMe);
  } catch (error) {
    console.error('[API] saveAuthData: 保存数据时出错', error);
    // 尝试保存简化版本，避免可能的存储限制问题
    try {
      const simpleAuthData = {
        user: { email: userData.email, id: userData.id },
        token: token,
        timestamp: Date.now()
      };
      localStorage.setItem('authData', JSON.stringify(simpleAuthData));
      localStorage.setItem('auth_remember', rememberMe.toString());
      console.log('[API] saveAuthData: 已保存简化版认证数据');
    } catch (fallbackError) {
      console.error('[API] saveAuthData: 无法保存任何认证数据', fallbackError);
    }
  }
};

/**
 * 清除用户认证信息
 */
export const clearAuthData = () => {
  localStorage.removeItem('authData');
  localStorage.removeItem('auth_remember');
  // 为了向后兼容，也清除 sessionStorage
  sessionStorage.removeItem('authData');
  
  console.log('[API] clearAuthData: 认证数据已清除');
};

/**
 * 解析JWT Token的payload
 * @param {string} token - JWT token
 * @returns {Object|null} payload对象或null
 */
const parseJwtPayload = (token) => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * 检查JWT Token是否过期
 * @param {string} token - JWT token
 * @returns {boolean} 是否过期
 */
const isTokenExpired = (token) => {
  try {
    const payload = parseJwtPayload(token);
    if (!payload || !payload.exp) {
      console.warn('[API] isTokenExpired: Token无效或缺少exp字段');
      return true;
    }
    // exp 是秒级时间戳，转换为毫秒
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();
    const isExpired = currentTime >= expirationTime;
    console.log(`[API] isTokenExpired: exp=${new Date(expirationTime).toISOString()}, now=${new Date(currentTime).toISOString()}, expired=${isExpired}`);
    return isExpired;
  } catch (e) {
    console.warn('[API] isTokenExpired: 解析Token失败', e);
    return true;
  }
};

/**
 * 获取认证令牌
 * @returns {string|null} JWT 令牌，如果未登录则返回 null
 */
export const getAuthToken = () => {
  console.log('[API] getAuthToken: 开始检查认证令牌...');
  try {
    // 现在我们只从 localStorage 获取
    const authDataStr = localStorage.getItem('authData');
    const rememberMe = localStorage.getItem('auth_remember') === 'true';
    
    console.log('[API] getAuthToken: 存储状态检查 -', {
      authData: authDataStr ? '存在' : '不存在',
      rememberMe: rememberMe
    });
    
    if (!authDataStr) {
      return null;
    }
    
    try {
      const authData = JSON.parse(authDataStr);
      
      // 检查令牌是否存在
      if (!authData.token) {
        console.warn('[API] getAuthToken: 认证数据格式不正确，缺少令牌字段');
        return null;
      }
      
      // 检查令牌格式
      if (typeof authData.token !== 'string' || !authData.token.trim()) {
        console.warn('[API] getAuthToken: 令牌格式无效');
        return null;
      }
      
      // 检查JWT Token是否过期
      if (isTokenExpired(authData.token)) {
        console.warn('[API] getAuthToken: JWT Token已过期');
        clearAuthData();
        // 触发token过期事件，让ApiInterceptor处理重定向
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('token-expired', {
            detail: { message: '登录已过期，请重新登录' }
          }));
        }
        return null;
      }
      
      // 如果用户未选择"记住我"，则添加会话超时检查（30分钟）
      if (!rememberMe && authData.timestamp) {
        const sessionTimeout = 30 * 60 * 1000; // 30分钟（毫秒）
        const currentTime = Date.now();
        const sessionAge = currentTime - authData.timestamp;
        
        if (sessionAge > sessionTimeout) {
          console.log('[API] getAuthToken: 会话已超时（未选择记住登录状态）');
          clearAuthData();
          return null;
        }
        
        // 更新时间戳以延长会话
        authData.timestamp = currentTime;
        localStorage.setItem('authData', JSON.stringify(authData));
      }
      
      return authData.token;
    } catch (e) {
      console.error('[API] getAuthToken: 解析认证数据失败', e);
      
      // 清除损坏的数据
      clearAuthData();
      
      return null;
    }
  } catch (error) {
    console.error('[API] getAuthToken: 访问存储出错', error);
    return null;
  }
};

/**
 * 获取当前用户信息
 * @returns {Object|null} 用户数据，如果未登录则返回 null
 */
export const getCurrentUser = () => {
  // 先检查 Token 是否有效（这会处理过期等情况）
  const token = getAuthToken();
  if (!token) {
    return null;
  }
  
  // 从 localStorage 获取
  const authDataStr = localStorage.getItem('authData');
  
  if (!authDataStr) {
    return null;
  }
  
  try {
    const authData = JSON.parse(authDataStr);
    return authData.user || null;
  } catch (e) {
    console.error('[API] getCurrentUser: 解析认证数据失败', e);
    return null;
  }
};

/**
 * 添加认证头信息到请求
 * @param {Object} options - 请求选项
 * @returns {Object} 添加了认证头的请求选项
 */
export const addAuthHeaders = (options = {}) => {
  const token = getAuthToken();
  
  if (token) {
    // 创建新的选项对象以避免修改原始对象
    const newOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    };
    return newOptions;
  }
  
  return options;
};

/**
 * API请求辅助函数
 * @param {string} endpoint - API端点
 * @param {Object|FormData} data - 请求数据（可以是JSON对象或FormData）
 * @param {boolean} withAuth - 是否添加认证头
 * @param {string} method - 请求方法（GET, POST, PUT, DELETE等）
 * @param {boolean} isFormData - 是否使用FormData格式（文件上传时需要设为true）
 * @returns {Promise} fetch请求Promise
 */
export const apiRequest = async (endpoint, data = {}, withAuth = true, method = 'GET', isFormData = false) => {
  let url = createApiUrl(endpoint);
  
  // 准备请求选项
  let fetchOptions = {
    method: method,
    headers: {},
    credentials: 'include',  // 发送跨域请求时携带cookie
    mode: 'cors',           // 明确指定CORS模式
  };
  
  // 如果不是FormData，设置Content-Type为application/json
  if (!isFormData) {
    fetchOptions.headers['Content-Type'] = 'application/json';
  }
  
  // 添加请求体（如果有）
  if (method !== 'GET' && method !== 'HEAD') {
    if (isFormData) {
      // 如果是FormData对象，直接使用
      fetchOptions.body = data;
    } else {
      // 否则转换为JSON字符串
      fetchOptions.body = JSON.stringify(data);
    }
  } else if (method === 'GET' && Object.keys(data).length > 0) {
    // 对于GET请求，将数据添加到URL作为查询参数
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      params.append(key, value);
    });
    const queryString = params.toString();
    if (queryString) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}${queryString}`;
    }
  }
  
  // 添加认证头（如果需要）
  if (withAuth) {
    // 检查token是否存在，如果不存在则直接抛出认证错误
    const token = getAuthToken();
    if (!token) {
      console.warn('[API] apiRequest: Token不存在，跳过请求');
      // 直接跳转到登录页
      window.location.href = '/dashboard/login?expired=1';
      const authError = new Error('未登录或登录已过期');
      authError.status = 401;
      authError.isAuthError = true;
      throw authError;
    }
    fetchOptions = addAuthHeaders(fetchOptions);
  }

  try {
    console.log(`正在发送${method}请求到: ${url}`, { 
      isFormData, 
      hasBody: !!fetchOptions.body,
      headers: fetchOptions.headers 
    });
    
    const response = await fetch(url, fetchOptions);
    
    // 尝试解析响应JSON
    let responseData;
    try {
      responseData = await response.json();
    } catch (e) {
      responseData = { status: 'error', message: '无法解析响应数据' };
    }
    
    // 检查状态码
    if (!response.ok) {
      // 特殊处理 401 未授权错误
      if (response.status === 401) {
        // 清除认证数据
        clearAuthData();
        
        // 创建自定义错误对象
        const authError = new Error(responseData.message || '未登录或登录已过期');
        authError.status = 401;
        authError.data = responseData;
        authError.isAuthError = true; // 添加标志以便区分
        throw authError;
      }
      
      // 其他错误
      const error = new Error(responseData.message || responseData.error || `API请求失败: ${response.status}`);
      error.status = response.status;
      error.data = responseData;
      error.response = response;
      throw error;
    }
    
    return responseData;
  } catch (error) {
    console.error('API请求错误:', error);
    throw error;
  }
};

export default {
  API_BASE_URL,
  API_ENDPOINTS,
  createApiUrl,
  apiRequest,
  addAuthHeaders,
  clearAuthData,
  saveAuthData,
  getAuthToken,
  getCurrentUser,
  getAuthConfig,
}; 